import {
  infiniteQueryOptions,
  type QueryFunctionContext,
} from '@tanstack/react-query';

import { fetchBuyerActiveOffers, fetchBuyerInactiveOffers } from './buyers/api';
import { detectInvalidOfferStatuses } from './buyers/helpers/detectInvalidOfferStatuses';
import { type OfferResponse } from './buyers/types';
import { type SellerProductOffersResponse } from './sellers/aliases';
import { getProductOffers } from './sellers/api';

import {
  RQ_BUYER_ACTIVE_OFFERS,
  RQ_BUYER_INACTIVE_OFFERS,
  RQ_SELLER_OFFERS_FOR_PRODUCT,
} from '@/modules/ReactQuery/cacheKeys';

function getNextOffersPageParam(
  lastPage: OfferResponse | SellerProductOffersResponse
) {
  return lastPage?.page_info?.has_more ? lastPage?.page_info?.last : undefined;
}

export function activeOffersInfiniteQueryOptions() {
  return infiniteQueryOptions({
    queryKey: [RQ_BUYER_ACTIVE_OFFERS],
    queryFn: async (ctx: QueryFunctionContext) => {
      const res = await fetchBuyerActiveOffers({
        after: ctx.pageParam as string | undefined,
      });
      const offers: OfferResponse = res.data;
      detectInvalidOfferStatuses(offers.objects ?? []);
      return offers;
    },
    getNextPageParam: getNextOffersPageParam,
    initialPageParam: undefined,
  });
}

export function inactiveOffersInfiniteQueryOptions() {
  return infiniteQueryOptions({
    queryKey: [RQ_BUYER_INACTIVE_OFFERS],
    queryFn: async (ctx: QueryFunctionContext) => {
      const res = await fetchBuyerInactiveOffers({
        after: ctx.pageParam as string | undefined,
      });
      const offers: OfferResponse = res.data;
      detectInvalidOfferStatuses(offers.objects ?? []);
      return offers;
    },
    getNextPageParam: getNextOffersPageParam,
    initialPageParam: undefined,
  });
}

export function sellerProductOffersInfiniteQueryOptions({
  productId,
  active,
  variantId,
}: {
  productId: string;
  active: boolean;
  variantId?: string;
}) {
  return infiniteQueryOptions({
    queryKey: [
      RQ_SELLER_OFFERS_FOR_PRODUCT,
      productId,
      active,
      variantId ?? null,
    ],
    queryFn: async (ctx: QueryFunctionContext) => {
      const res = await getProductOffers({
        productId,
        active,
        variantId,
        after: ctx.pageParam as string | undefined,
      });
      return res.data;
    },
    retry: false,
    getNextPageParam: getNextOffersPageParam,
    initialPageParam: undefined,
  });
}
